package dev.lingvo.shared.screenshots.scene

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageFit
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.NRect
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.Shadow
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.shared.screenshots.doc.StrokeSpec
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import kotlin.math.round

internal fun Layer.rebase(base: LayerBase): Layer = when (this) {
    is BackgroundLayer -> copy(base = base)
    is DeviceLayer -> copy(base = base)
    is ImageLayer -> copy(base = base)
    is TextLayer -> copy(base = base)
    is ShapeLayer -> copy(base = base)
    is GroupLayer -> copy(base = base)
}

internal fun applyScene(page: Page, input: SceneInput, placements: List<Placement>): Page {
    val sceneWidth = if (input.canvas.width > input.canvas.height) .58 * input.canvas.width - round(.06 * input.canvas.width)
        else input.canvas.width.toDouble()
    val byRole = placements.associateBy { it.role to it.roleSlot }
    val layers = page.layers.map { layer ->
        if (layer.base.role == null) layer else {
            val place = byRole[layer.base.role to layer.base.roleSlot]
            layer.rebase(layer.base.copy(box = place?.box ?: layer.base.box, hidden = place == null))
        }
    }.toMutableList()
    val ids = mutableSetOf<String>()
    fun collect(layer: Layer) { ids += layer.base.id; if (layer is GroupLayer) layer.children.forEach(::collect) }
    layers.forEach(::collect)
    fun fresh(role: Role, slot: Int): String {
        val stem = "scene-${role.name.lowercase()}-$slot"
        var number = 0
        // Layer ids are document scoped, whereas the same page id may occur on different boards.
        val namespace = page.layers.firstOrNull { it.base.role == Role.DEVICE_1 }?.base?.id
            ?: page.layers.firstOrNull { it.base.role == Role.CAPTION }?.base?.id
            ?: page.layers.firstOrNull()?.base?.id ?: page.id
        val compact = "s-${namespaceDigest("$stem:$namespace")}"
        var id = "$stem-$namespace".takeIf { it.length <= 40 } ?: compact
        while (!ids.add(id)) { number++; id = "${compact.take(36)}-$number" }
        return id
    }
    for (place in placements) {
        if (layers.any { it.base.role == place.role && it.base.roleSlot == place.roleSlot }) continue
        val device = page.layers.filterIsInstance<DeviceLayer>().firstOrNull { it.base.role == Role.DEVICE_1 }
        val caption = page.layers.filterIsInstance<TextLayer>().firstOrNull { it.base.role == Role.CAPTION }
        val base = LayerBase(fresh(place.role, place.roleSlot), box = place.box, role = place.role, roleSlot = place.roleSlot)
        val layer: Layer = when (place.role) {
            Role.DEVICE_1, Role.DEVICE_2, Role.DEVICE_3 -> {
                val offset = when (place.role) { Role.DEVICE_2 -> 1; Role.DEVICE_3 -> 2; else -> place.roleSlot }
                if (device == null) continue
                device.copy(base = device.base.copy(id = base.id, box = base.box, role = base.role, roleSlot = base.roleSlot, hidden = false),
                    screen = ImageSource.Capture(slot = 0, planOffset = offset))
            }
            Role.CAPTION -> if (caption == null) continue else caption.copy(base = caption.base.copy(id = base.id, box = base.box,
                roleSlot = base.roleSlot, hidden = false), content = TextContent.SlotCaption(place.roleSlot))
            Role.SCRIM -> ShapeLayer(base, ShapeKind.RECT, fill = Paint.Linear(180.0, listOf(Stop(0.0, "@bg/0"), Stop(.45, "@bg/0.92"))))
            Role.CARD_1, Role.CARD_2 -> ImageLayer(base.copy(shadow = Shadow(0.0, .012 * sceneWidth, .04 * sceneWidth, "#0000004D")),
                device?.screen ?: ImageSource.Capture(), ImageFit.COVER,
                if (place.role == Role.CARD_1) NRect(0.0, .30, 1.0, .12) else NRect(0.0, .60, 1.0, .10),
                .03 * sceneWidth)
            Role.RIBBON -> ShapeLayer(base, ShapeKind.CURVE, stroke = StrokeSpec((.12 * input.canvas.width).coerceAtMost(200.0), Paint.Solid("@accent/0.9")),
                points = List(input.slots.coerceAtLeast(2)) { k -> Point((k + .5) * input.canvas.width, if (k % 2 == 0) .24 * input.canvas.height else 0.0) })
            else -> continue
        }
        val insert = layers.indexOfFirst { existing ->
            val z = byRole[existing.base.role to existing.base.roleSlot]?.z
            z != null && z > place.z
        }
        if (insert < 0) layers.add(layer) else layers.add(insert, layer)
    }
    val activeIndices = layers.indices.filter { !layers[it].base.hidden && (layers[it].base.role to layers[it].base.roleSlot) in byRole }
    val ordered = activeIndices.map { layers[it] }.sortedWith(compareBy<Layer> {
        byRole.getValue(requireNotNull(it.base.role) to it.base.roleSlot).z
    }.thenBy { it.base.role?.ordinal }.thenBy { it.base.roleSlot })
    activeIndices.zip(ordered).forEach { (index, layer) -> layers[index] = layer }
    return page.copy(scene = input.spec, layers = layers)
}
